/* global CarouselSlider */

if (!customElements.get('product-card-image-slider')) {
  customElements.whenDefined('carousel-slider').then(() => {
    class ProductCardImageSlider extends CarouselSlider {
      constructor() {
        super();
        this.productCard = this.closest('product-card');
        this.productCardSwatches = this.productCard.querySelectorAll('.card__swatches .opt-btn');
        this.slides = this.querySelectorAll('.slider__item');

        this.productCard.addEventListener('change', this.handleSwatchChange.bind(this));
      }

      init() {
        super.init();

        this.slider.addEventListener('scroll', this.scrollInProgress.bind(this));

        // Cache scrollbar thumb element
        this.scrollbarThumb = this.querySelector('.card-slider__thumb');

        // If swatches are enabled, mark the swatch anchors in the slideshow
        if (this.productCardSwatches.length) {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          const activeSwatchId = this.productCard.querySelector('.card__swatches .opt-btn:checked')?.dataset.mediaId || activeSlide?.dataset.mediaId;
          let activeSwatchSlide;

          if (activeSwatchId) {
            this.productCardSwatches.forEach((swatch) => {
              const swatchSlide = this.querySelector(`[data-media-id="${swatch.dataset.mediaId}"]`);
              if (swatchSlide) {
                swatchSlide.setAttribute('data-swatch-anchor', 'true');

                // Set the active set of slideshow images
                if (swatchSlide.dataset.mediaId === activeSwatchId) {
                  activeSlide.removeAttribute('aria-current');
                  swatchSlide.setAttribute('aria-current', 'true');
                  activeSwatchSlide = swatchSlide;
                }
              }
            });
          }

          if (activeSwatchSlide) this.handleSwatchChange(null, activeSwatchSlide.dataset.mediaId);
        } else {
          // Show the current slider item
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          if (activeSlide) {
            this.handleSwatchChange(null, activeSlide.dataset.mediaId);
          } else {
            this.slider.slides[1]?.setAttribute('aria-current', 'true');
            this.limitMobileSlides();
            this.syncScrollbarToVisibleSlides();
          }
        }

        // Set initial scrollbar position
        this.updateScrollbar(0);
      }

      /**
       * Moves the scrollbar thumb to reflect the current slide.
       * translateX is relative to the thumb's own width, so index * 100%
       * places it exactly at the correct position.
       * @param {number} index - Index among visible slides.
       */
      updateScrollbar(index) {
        if (!this.scrollbarThumb) return;
        this.scrollbarThumb.style.transform = `translateX(${index * 100}%)`;
      }

      /**
       * Recalculates the thumb width based on the number of visible slides
       * and resets its position to the start.
       */
      syncScrollbarToVisibleSlides() {
        if (!this.scrollbarThumb) return;
        const visibleCount = this.slides.filter((s) => s.hidden !== true).length;
        this.scrollbarThumb.style.width = visibleCount > 0 ? `${100 / visibleCount}%` : '100%';
        this.updateScrollbar(0);
      }

      /**
       * On mobile viewports, limits visible slides to a maximum of 2.
       * Works with the hidden attribute so it's compatible with swatch filtering.
       */
      limitMobileSlides() {
        if (window.matchMedia('(min-width: 600px)').matches) return;
        let visibleCount = 0;
        this.slides.forEach((slide) => {
          if (slide.hidden) return;
          visibleCount++;
          if (visibleCount > 2) {
            slide.toggleAttribute('hidden', true);
          }
        });
      }

      /**
       * Updates the visibility of slides based on the state of card swatches.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      updateSlideVisibility(mediaId) {
        let hideSlide = true;
        let foundMediaId = false;
        this.slides.forEach((slide) => {
          if (!foundMediaId && slide.getAttribute('data-media-id') === mediaId) {
            hideSlide = false;
            foundMediaId = true;
          } else if (foundMediaId && slide.hasAttribute('data-swatch-anchor')) {
            hideSlide = true;
          }

          slide.toggleAttribute('hidden', hideSlide);
        });

        this.limitMobileSlides();
        this.syncScrollbarToVisibleSlides();
        this.setButtonStates();
      }

      /**
       * Handles 'change' events in the product card swatches.
       * @param {object} evt - Event object.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      handleSwatchChange(evt, mediaId) {
        const swatchMediaId = evt?.target.dataset.mediaId || mediaId;
        if (swatchMediaId) {
          this.updateSlideVisibility(swatchMediaId);

          const variantMedia = this.querySelector(`[data-media-id="${swatchMediaId}"]`);
          if (variantMedia) {
            const left = variantMedia.closest('.slider__item').offsetLeft;
            this.slider.scrollTo({ left, behavior: 'instant' });
          }
        }
      }

      /**
       * Sets the disabled state of the nav buttons.
       */
      setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) {
          return;
        }

        const currentSlideIndex = Math.round(this.slider.scrollLeft / this.slideSpan) + 1;
        const visibleSlideCount = this.slides.filter((slide) => slide.hidden !== true).length;

        this.prevBtn.disabled = currentSlideIndex === 1
          || (this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0);
        this.nextBtn.disabled = visibleSlideCount === currentSlideIndex
          || this.getSlideVisibility(this.slides[this.slides.length - 1]);
      }

      /**
       * Handles every scroll event (non-debounced) for real-time scrollbar tracking.
       */
      scrollInProgress() {
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');

        // Sync scrollbar thumb in real-time with finger movement.
        // Uses actual rendered dimensions so it works regardless of
        // how the thumb width is set (CSS override on mobile, inline, swatch filter).
        if (this.scrollbarThumb) {
          const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
          if (maxScroll > 0) {
            const progress = this.slider.scrollLeft / maxScroll;
            const thumbW = this.scrollbarThumb.offsetWidth;
            const trackW = this.scrollbarThumb.parentElement.offsetWidth;
            if (thumbW > 0) {
              const maxTranslate = (trackW / thumbW - 1) * 100;
              this.scrollbarThumb.style.transform = `translateX(${progress * maxTranslate}%)`;
            }
          }
        }
      }

      /**
       * Handles debounced scroll events (after scrolling stops).
       */
      handleScroll() {
        super.handleScroll();
        this.slides[this.currentIndex].setAttribute('aria-current', 'true');
      }
    }

    customElements.define('product-card-image-slider', ProductCardImageSlider);
  });
}
